import { useMemo, useState } from "react";
import { ArrowDown, ArrowUp, Brain, Search } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useModelRuns, useModels, useRunPredictions } from "@/hooks/api";
import { errorRowBackground } from "@/lib/error-color";
import { cn } from "@/lib/utils";
import { formatPct } from "@/lib/format";
import type { PredictionOut } from "@/api/client";

type SortKey =
  | "primary_symbol"
  | "company_name"
  | "sector"
  | "actual_change_pct"
  | "predicted_change_pct"
  | "error_abs";
type SortDir = "asc" | "desc";

export function PredictionsMain({ slug, date }: { slug: string | null; date: string }) {
  const { data: models } = useModels();
  const { data: runs } = useModelRuns(slug, date);
  const runId = runs?.[0]?.id ?? null;
  const { data: predictions, isLoading } = useRunPredictions(slug, runId);

  const model = models?.find((m) => m.slug === slug);

  const [sortKey, setSortKey] = useState<SortKey>("error_abs");
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const [filter, setFilter] = useState("");

  const sorted = useMemo(() => {
    const arr = (predictions ?? []).filter((p) => {
      if (!filter) return true;
      const f = filter.toLowerCase();
      return (
        p.primary_symbol?.toLowerCase().includes(f) ||
        p.company_name.toLowerCase().includes(f)
      );
    });
    arr.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      let cmp: number;
      if (av === null || av === undefined) cmp = bv === null || bv === undefined ? 0 : 1;
      else if (bv === null || bv === undefined) cmp = -1;
      else if (typeof av === "string" && typeof bv === "string") cmp = av.localeCompare(bv);
      else cmp = (av as number) - (bv as number);
      return sortDir === "asc" ? cmp : -cmp;
    });
    return arr;
  }, [predictions, filter, sortKey, sortDir]);

  function toggleSort(k: SortKey) {
    if (k === sortKey) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(k);
      setSortDir(k === "primary_symbol" ? "asc" : "desc");
    }
  }

  if (!slug) {
    return (
      <div className="p-8 text-muted-foreground text-sm">
        Select a model in the sidebar to see its run.
      </div>
    );
  }
  if (!model) {
    return <div className="p-8 text-muted-foreground text-sm">Loading…</div>;
  }

  const total = predictions?.length ?? 0;
  const correctDir = (predictions ?? []).filter((p) => p.direction_correct === true).length;

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="px-3 md:px-4 py-3 border-b border-border">
        <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between md:gap-4">
          <div className="min-w-0">
            <div className="flex items-baseline gap-2 flex-wrap">
              <div className="font-mono font-bold text-[16px]">{model.name}</div>
              <Badge variant="outline">{model.slug}</Badge>
            </div>
            {model.description && (
              <div className="text-[11px] text-muted-foreground mt-1 max-w-2xl">
                {model.description}
              </div>
            )}
          </div>
          <Button variant="outline" size="sm" disabled className="self-start md:self-auto">
            <Brain className="h-3 w-3" /> Re-run prediction
          </Button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3">
          <MetricCard
            label="Training"
            value={
              model.train_start && model.train_end
                ? `${model.train_start} → ${model.train_end}`
                : "—"
            }
            mono
          />
          <MetricCard
            label="Horizon"
            value={
              runs && runs[0]
                ? `${runs[0].horizon_start} → ${runs[0].horizon_end}`
                : "—"
            }
            mono
          />
          <MetricCard label="Universe" value={total === 0 ? "—" : `${total} tickers`} mono />
          <MetricCard
            label="Correct direction"
            value={total === 0 ? "—" : `${correctDir}/${total}`}
            mono
          />
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 mt-2">
          <MetricCard
            label="Accuracy"
            value={
              model.accuracy === null || model.accuracy === undefined
                ? "—"
                : `${(model.accuracy * 100).toFixed(1)}%`
            }
            pos={
              model.accuracy === null || model.accuracy === undefined
                ? undefined
                : model.accuracy > 0.55
            }
          />
          <MetricCard
            label="MAE"
            value={model.mae?.toFixed(2) ?? "—"}
            suffix={model.mae !== null && model.mae !== undefined ? "%" : ""}
          />
          <MetricCard
            label="RMSE"
            value={model.rmse?.toFixed(2) ?? "—"}
            suffix={model.rmse !== null && model.rmse !== undefined ? "%" : ""}
          />
          <MetricCard label="Precision" value={model.precision?.toFixed(3) ?? "—"} />
          <MetricCard label="Recall" value={model.recall?.toFixed(3) ?? "—"} />
        </div>
      </div>

      <div className="flex-1 overflow-hidden flex flex-col">
        <div className="px-3 py-1.5 border-b border-border bg-muted/20 flex flex-col gap-1.5 md:flex-row md:items-center md:justify-between md:gap-2">
          <div className="text-[11px] font-mono text-muted-foreground">
            PER-COMPANY RESULTS · {sorted.length} of {total}
          </div>
          <div className="relative w-full md:w-56">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
            <Input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter by ticker..."
              className="pl-7 h-6 text-[11px]"
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="px-3 py-6 text-center text-[12px] text-muted-foreground">Loading…</div>
          ) : runId === null ? (
            <div className="px-3 py-6 text-center text-[12px] text-muted-foreground">
              No prediction runs found for {date}.
            </div>
          ) : total === 0 ? (
            <div className="px-3 py-6 text-center text-[12px] text-muted-foreground">
              This run produced no per-company predictions.
            </div>
          ) : (
            <>
              <div className="md:hidden">
                {sorted.map((p) => (
                  <PredictionMobileCard key={p.company_id} prediction={p} />
                ))}
              </div>
              <Table className="hidden md:table text-[11px]">
                <TableHeader>
                  <TableRow>
                    <TH col="primary_symbol" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} width={60}>
                      Ticker
                    </TH>
                    <TH col="company_name" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}>
                      Company
                    </TH>
                    <TH col="sector" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} width={120}>
                      Sector
                    </TH>
                    <TH col="actual_change_pct" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} width={80} align="right">
                      Actual
                    </TH>
                    <TH col="predicted_change_pct" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} width={80} align="right">
                      Predicted
                    </TH>
                    <TH col="error_abs" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} width={80} align="right">
                      Error
                    </TH>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sorted.map((p) => (
                    <PredictionRow key={p.company_id} prediction={p} />
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function PredictionRow({ prediction: p }: { prediction: PredictionOut }) {
  const bg = errorRowBackground(p.error_abs);
  const actualPos = (p.actual_change_pct ?? 0) >= 0;
  const predPos = p.predicted_change_pct >= 0;
  return (
    <TableRow className="hover:bg-accent/40" style={bg ? { background: bg } : undefined}>
      <TableCell className="py-1.5 font-mono font-semibold">{p.primary_symbol ?? "—"}</TableCell>
      <TableCell className="py-1.5 truncate max-w-[200px]">{p.company_name}</TableCell>
      <TableCell className="py-1.5 text-muted-foreground">{p.sector ?? "—"}</TableCell>
      <TableCell
        className={cn(
          "py-1.5 font-mono tabular-nums text-right",
          p.actual_change_pct === null
            ? "text-muted-foreground"
            : actualPos
              ? "text-[hsl(var(--chart-up))]"
              : "text-[hsl(var(--chart-down))]",
        )}
      >
        {formatPct(p.actual_change_pct)}
      </TableCell>
      <TableCell
        className={cn(
          "py-1.5 font-mono tabular-nums text-right",
          predPos ? "text-[hsl(var(--chart-up))]" : "text-[hsl(var(--chart-down))]",
        )}
      >
        {formatPct(p.predicted_change_pct)}
      </TableCell>
      <TableCell
        className={cn(
          "py-1.5 font-mono tabular-nums text-right",
          p.direction_correct === false && "text-muted-foreground",
        )}
      >
        {p.error_abs?.toFixed(2) ?? "—"}
      </TableCell>
    </TableRow>
  );
}

function PredictionMobileCard({ prediction: p }: { prediction: PredictionOut }) {
  const bg = errorRowBackground(p.error_abs);
  const actualPos = (p.actual_change_pct ?? 0) >= 0;
  const predPos = p.predicted_change_pct >= 0;
  return (
    <div
      className="block px-3 py-2 border-b border-border/60 min-h-[44px] hover:bg-accent/40"
      style={bg ? { background: bg } : undefined}
    >
      <div className="flex items-baseline justify-between gap-2">
        <div className="flex items-baseline gap-2 min-w-0">
          <span className="font-mono font-semibold text-[12px] shrink-0">
            {p.primary_symbol ?? "—"}
          </span>
          <span className="text-[11px] text-muted-foreground truncate">{p.company_name}</span>
        </div>
        <span
          className={cn(
            "font-mono text-[11px] tabular-nums shrink-0",
            p.direction_correct === false && "text-muted-foreground",
          )}
        >
          err {p.error_abs?.toFixed(2) ?? "—"}
        </span>
      </div>
      <div className="flex items-baseline justify-between gap-2 mt-0.5 text-[10px] font-mono text-muted-foreground">
        <span className="truncate">{p.sector ?? "—"}</span>
        <span className="shrink-0">
          act{" "}
          <span
            className={
              p.actual_change_pct === null
                ? "text-muted-foreground"
                : actualPos
                  ? "text-[hsl(var(--chart-up))]"
                  : "text-[hsl(var(--chart-down))]"
            }
          >
            {formatPct(p.actual_change_pct)}
          </span>
          {" "}· pred{" "}
          <span
            className={
              predPos ? "text-[hsl(var(--chart-up))]" : "text-[hsl(var(--chart-down))]"
            }
          >
            {formatPct(p.predicted_change_pct)}
          </span>
        </span>
      </div>
    </div>
  );
}

function MetricCard({
  label,
  value,
  pos,
  mono,
  suffix,
}: {
  label: string;
  value: string;
  pos?: boolean;
  mono?: boolean;
  suffix?: string;
}) {
  return (
    <div className="border border-border rounded-[4px] px-2 py-1.5 bg-card">
      <div className="text-[9px] uppercase tracking-wider text-muted-foreground font-mono">
        {label}
      </div>
      <div
        className={cn(
          "text-[12px] mt-0.5 tabular-nums",
          mono ? "font-mono" : "font-semibold",
          pos === true && "text-[hsl(var(--chart-up))]",
          pos === false && "text-[hsl(var(--chart-down))]",
        )}
      >
        {value}
        {suffix ?? ""}
      </div>
    </div>
  );
}

function TH({
  col,
  sortKey,
  sortDir,
  onSort,
  width,
  align,
  children,
}: {
  col: SortKey;
  sortKey: SortKey;
  sortDir: SortDir;
  onSort: (k: SortKey) => void;
  width?: number;
  align?: "right";
  children: React.ReactNode;
}) {
  return (
    <TableHead
      style={{ width }}
      onClick={() => onSort(col)}
      className={cn(
        "h-7 px-2 cursor-pointer text-[10px] uppercase tracking-wider text-muted-foreground hover:text-foreground bg-muted/30",
        align === "right" && "text-right",
      )}
    >
      <span className={cn("inline-flex items-center gap-1", align === "right" && "flex-row-reverse")}>
        {children}
        {sortKey === col &&
          (sortDir === "asc" ? <ArrowUp className="h-2.5 w-2.5" /> : <ArrowDown className="h-2.5 w-2.5" />)}
      </span>
    </TableHead>
  );
}
